import { onMounted, onUnmounted, onBeforeMount, ref } from "vue";
import throttle from "lodash/throttle";
import useSizes from "./useSizes";
import { interpolation } from "~/utils/helpers";
export default function useElementPosition(usedInVue = true) {
    const { windowHeight, nativeInit: scrollNativeInit } = useSizes(usedInVue);
    const observable = ref(null);
    const aboveViewport = ref(false);
    const belowViewport = ref(true);
    const relativeViewportPosition = ref(0);
    /**
     * Get the element position related to the viewport.
     *
     * Where 0 is when the top of the element is at the bottom of the screen (before moving in).
     * And 1 is when th bottom of the element is at the top of the screen (just moved out).
     */
    const elementRelatedInViewport = () => {
        if (!observable.value)
            return;
        const elementBoundingClient = observable.value.getBoundingClientRect();
        if (elementBoundingClient.top > windowHeight.value) {
            aboveViewport.value = false;
            belowViewport.value = true;
            relativeViewportPosition.value = 0;
            return;
        }
        if (elementBoundingClient.top < (observable.value.clientHeight * -1)) {
            aboveViewport.value = true;
            belowViewport.value = false;
            relativeViewportPosition.value = 1;
            return;
        }
        aboveViewport.value = false;
        belowViewport.value = false;
        relativeViewportPosition.value = interpolation(elementBoundingClient.top, windowHeight.value, observable.value.clientHeight * -1, 0, 1);
    };
    /**
     * Get the element position related to the viewport.
     *
     * Where 0 is when the top of the element is at the bottom of the screen (before moving in).
     * And 1 is when th bottom of the element is at the top of the screen (just moved out).
     */
    const givenElementRelatedInViewport = (el) => {
        const elementBoundingClient = el.getBoundingClientRect();
        if (elementBoundingClient.top > windowHeight.value) {
            return {
                aboveViewport: false,
                belowViewport: true,
                relativeViewportPosition: 0
            };
        }
        if (elementBoundingClient.top < (el.clientHeight * -1)) {
            return {
                aboveViewport: true,
                belowViewport: false,
                relativeViewportPosition: 1
            };
        }
        return {
            aboveViewport: false,
            belowViewport: false,
            relativeViewportPosition: interpolation(elementBoundingClient.top, windowHeight.value, el.clientHeight * -1, 0, 1)
        };
    };
    // Use throttled of 16ms, this will result in 60fps
    let scrolled = throttle(elementRelatedInViewport, 16);
    if (usedInVue) {
        onBeforeMount(() => {
            elementRelatedInViewport();
        });
        onMounted(() => {
            window.addEventListener("scroll", scrolled, { passive: true });
        });
        onUnmounted(() => {
            window.removeEventListener("scroll", scrolled);
        });
    }
    const nativeInit = () => {
        window.addEventListener("scroll", scrolled, { passive: true });
        elementRelatedInViewport();
        scrollNativeInit();
    };
    return {
        observable,
        aboveViewport,
        belowViewport,
        relativeViewportPosition,
        nativeInit,
        givenElementRelatedInViewport,
    };
}
